import React, { useEffect, useState } from 'react';
import { Copy, KeyRound, ShieldCheck, Smartphone } from 'lucide-react';
import * as api from '../api';

// The second sign-in step for accounts where an administrator has required two-step sign-in.
//   mode "verify": enrolled — the 6-digit authenticator code (or a one-time backup code).
//   mode "setup":  first time — scan the QR code, confirm with a code, then save the backup codes.
// `onDone(tokenResponse)` finishes the sign-in exactly as a plain password login would.

const BLUE = '#6490f6ff';
const inputStyle = {
  width: '100%', padding: '12px 14px', fontSize: 22, letterSpacing: '0.35em', textAlign: 'center', fontWeight: 700,
  border: `1px solid ${BLUE}`, background: '#1e1e24', color: 'white', borderRadius: 11, outline: 'none', fontFamily: 'var(--mono, monospace)',
};
const buttonStyle = (off) => ({
  width: '100%', padding: '12px 0', fontSize: 15, fontWeight: 'bold', background: BLUE, color: '#060505ff', border: 'none',
  borderRadius: 11, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.4 : 1, fontFamily: 'inherit',
});
const linkStyle = { background: 'none', border: 'none', color: BLUE, fontWeight: 700, cursor: 'pointer', fontSize: 13, padding: 0, fontFamily: 'inherit' };
const note = { fontSize: 13, color: 'rgba(255,255,255,0.72)', lineHeight: 1.6, margin: 0, textAlign: 'center' };

function Message({ text }) {
  if (!text) return null;
  return (
    <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 14, background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.3)' }}>
      {text}
    </div>
  );
}

export default function MfaSignIn({ mode, mfaToken, onDone, onCancel }) {
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [setup, setSetup] = useState(null);          // { secret, qr_svg }
  const [finished, setFinished] = useState(null);    // token response holding backup codes, shown once
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (mode !== 'setup') return;
    api.mfaSetup(mfaToken).then(setSetup).catch(e => setError(e.message || 'Could not start the set-up. Sign in again.'));
  }, [mode, mfaToken]);

  const digits = code.replace(/\D/g, '');
  const ready = useBackup ? code.replace(/[^A-Za-z0-9]/g, '').length === 8 : digits.length === 6;

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setError('');
    try {
      if (mode === 'setup') {
        const res = await api.mfaSetupConfirm(mfaToken, digits);
        setFinished(res);
      } else {
        onDone(await api.mfaVerify(mfaToken, useBackup ? code.trim() : digits));
      }
    } catch (e) {
      setError(e.message || 'That code is not right.');
      setCode('');
    }
    setBusy(false);
  };

  const onKey = (e) => { if (e.key === 'Enter') submit(); };

  // ── after enrolment: the backup codes, once ────────────────────────────
  if (finished) {
    const codes = finished.backup_codes || [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ textAlign: 'center', color: '#86efac' }}><ShieldCheck size={34} /></div>
        <h3 style={{ margin: 0, textAlign: 'center', color: 'white', fontSize: 18 }}>Two-step sign-in is on</h3>
        <p style={note}>Save these backup codes somewhere safe. Each one signs you in once if you lose your phone. <b>They will not be shown again.</b></p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: 12, borderRadius: 10, background: '#1e1e24', border: `1px solid ${BLUE}` }}>
          {codes.map(c => <code key={c} style={{ color: 'white', fontSize: 15, textAlign: 'center', letterSpacing: '0.08em' }}>{c}</code>)}
        </div>
        <button style={{ ...linkStyle, alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={() => { navigator.clipboard?.writeText(codes.join('\n')).then(() => setCopied(true)).catch(() => {}); }}>
          <Copy size={14} /> {copied ? 'Copied' : 'Copy codes'}
        </button>
        <button style={buttonStyle(false)} onClick={() => onDone(finished)}>I have saved them — continue</button>
      </div>
    );
  }

  // ── set-up: scan, then confirm with the first code ─────────────────────
  if (mode === 'setup') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ textAlign: 'center', color: BLUE }}><Smartphone size={30} /></div>
        <h3 style={{ margin: 0, textAlign: 'center', color: 'white', fontSize: 18 }}>Set up two-step sign-in</h3>
        <p style={note}>Your administrator requires an authenticator app for this account. Open Google Authenticator,
          Microsoft Authenticator or a similar app, add an account, and scan this code.</p>
        {setup ? (
          <>
            <div style={{ alignSelf: 'center', width: 196, height: 196, background: 'white', borderRadius: 12, padding: 8 }}
                 dangerouslySetInnerHTML={{ __html: setup.qr_svg.replace('<svg', '<svg width="180" height="180"') }} />
            <p style={{ ...note, fontSize: 12 }}>Can't scan? Enter this key in the app:<br />
              <code style={{ color: 'white', fontSize: 13, letterSpacing: '0.08em', wordBreak: 'break-all' }}>{setup.secret.match(/.{1,4}/g).join(' ')}</code></p>
          </>
        ) : !error && <p style={note}>Preparing your code…</p>}
        <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>Then enter the 6-digit code the app shows</label>
        <input value={code} onChange={e => setCode(e.target.value)} onKeyDown={onKey} inputMode="numeric" autoComplete="one-time-code"
               maxLength={7} placeholder="000000" style={inputStyle} disabled={!setup || busy} autoFocus />
        <Message text={error} />
        <button style={buttonStyle(!ready || busy || !setup)} disabled={!ready || busy || !setup} onClick={submit}>
          {busy ? 'Checking…' : 'Turn on two-step sign-in'}
        </button>
        <button style={{ ...linkStyle, alignSelf: 'center' }} onClick={onCancel}>Back to sign in</button>
      </div>
    );
  }

  // ── sign-in with a code ────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ textAlign: 'center', color: BLUE }}>{useBackup ? <KeyRound size={30} /> : <Smartphone size={30} />}</div>
      <h3 style={{ margin: 0, textAlign: 'center', color: 'white', fontSize: 18 }}>Two-step sign-in</h3>
      <p style={note}>{useBackup ? 'Enter one of the backup codes you saved when you set up the app. Each works once.'
                                 : 'Enter the 6-digit code from your authenticator app.'}</p>
      <input value={code} onChange={e => setCode(e.target.value)} onKeyDown={onKey}
             inputMode={useBackup ? 'text' : 'numeric'} autoComplete="one-time-code" autoFocus
             maxLength={useBackup ? 9 : 7} placeholder={useBackup ? 'XXXX-XXXX' : '000000'}
             style={{ ...inputStyle, letterSpacing: useBackup ? '0.15em' : '0.35em' }} disabled={busy} />
      <Message text={error} />
      <button style={buttonStyle(!ready || busy)} disabled={!ready || busy} onClick={submit}>{busy ? 'Checking…' : 'Verify'}</button>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button style={linkStyle} onClick={() => { setUseBackup(b => !b); setCode(''); setError(''); }}>
          {useBackup ? 'Use the authenticator app' : 'Lost your phone? Use a backup code'}
        </button>
        <button style={linkStyle} onClick={onCancel}>Back</button>
      </div>
      <p style={{ ...note, fontSize: 12 }}>No phone and no backup codes? Contact the Administrator to reset your two-step sign-in.</p>
    </div>
  );
}
